import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Widget } from '@typeform/embed-react';
import c from 'classnames';

import useProfileSurveys from 'services/surveys/useProfileSurveys';
import useSubmitFinishedSurvey from 'services/surveys/useSubmitFinishedSurvey';
import useFeature from 'services/features/useFeature';
import { EventPropertiesMap } from 'contexts/analytics/types';
import { getTinyAccountById } from 'store/account';
import { getKitById } from 'store/kits';
import { ProfileSurveys } from 'types/Surveys';

import {
  ActivationScreen,
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  DesignButton,
  ExitFormsWarningModal,
  Header,
  HealthFormsStepBar,
  HealthFormTransition,
  Typography,
} from 'components';

import styles from './TypeformSurvey.module.css';

// Fake loader between bundles per ENG-4942 — long enough to read "next up",
// short enough not to feel like a real wait (nothing is actually loading).
const NEXT_SURVEY_TRANSITION_DELAY_MS = 5000;

// Health history is always the 3rd (last) of the 3 top-level health-form
// bundles (Profile / Sample info / Health history) — see
// hooks/useHealthFormsSequence, which owns that ordering for the flows that
// actually need to know what's next (Profile.tsx's THSurvey). Here we only
// ever render *this* bundle's position, so it's a fixed constant rather than
// a live computation.
const HEALTH_FORMS_TOTAL = 3;
const HEALTH_HISTORY_INDEX = 2;

export type EventSurveyCompletedWithKit =
  EventPropertiesMap['Survey Completed'] & {
    kit_id: string; //kit_id is not undefined with the new profile flow, all are kit related surveys
  };

const TypeformSurvey = () => {
  const { tinyAccountId, kitId, typeformId } = useParams<{
    tinyAccountId: string;
    kitId: string;
    typeformId: string;
  }>();
  const navigate = useNavigate();
  const { data: surveys } = useProfileSurveys(tinyAccountId, kitId);
  const [surveyFinishedNow, setSurveyFinishedNow] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [nextSurveyToShow, setNextSurveyToShow] =
    useState<ProfileSurveys | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const onSubmitRef = useRef<() => Promise<void>>();
  const handleTypeformSubmit = useCallback(() => onSubmitRef.current?.(), []);
  const submitSurveyMutation = useSubmitFinishedSurvey();
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const tinyAccount = useSelector(getTinyAccountById(tinyAccountId));
  const kit = useSelector(getKitById(kitId));

  const currentTypeformSurvey = surveys?.find(
    survey => survey.typeform_survey_id === typeformId,
  );

  // `surveys` won't reflect this submission until the query refetches, so
  // the next bundle is determined by excluding the survey being submitted
  // right now rather than waiting on is_complete to flip.
  const nextIncompleteSurvey = surveys?.find(
    survey =>
      !survey.is_partner &&
      !survey.is_complete &&
      survey.typeform_survey_id !== typeformId,
  );

  // Same condition the "Finish later" footer below renders on — kept as one
  // value so the widget's height reservation for that footer can't drift
  // out of sync with whether the footer is actually there.
  const hasFinishLaterFooter =
    isRedesignedActivationEnabled && !nextSurveyToShow && !!nextIncompleteSurvey;

  // Reset local per-survey state whenever we land on a new typeform id,
  // whether from auto-progression or a direct link.
  useEffect(() => {
    setSurveyFinishedNow(false);
    setNextSurveyToShow(null);
  }, [typeformId]);

  useEffect(() => {
    return () => clearTimeout(transitionTimeoutRef.current);
  }, []);

  const isSurveyCompleted = useMemo(() => {
    return !!typeformId && !!currentTypeformSurvey?.is_complete;
  }, [currentTypeformSurvey, typeformId]);

  const formProperties = useMemo(() => {
    if (!tinyAccountId || !kitId || !currentTypeformSurvey) return {};
    const properties = {} as Record<string, string>;
    currentTypeformSurvey?.form_properties.map(property => {
      properties[property.hidden_field] = property.value;
    });
    return properties;
  }, [tinyAccountId, kitId, currentTypeformSurvey]);

  if (
    !typeformId ||
    !kitId ||
    !tinyAccountId ||
    !tinyAccount ||
    (!!kitId && (!kit || kit.tinyaccount_id !== tinyAccountId))
  ) {
    //No data, or the data doesn't belong to this mainaccount
    return null;
  }

  const onSubmitTypeform = async () => {
    const eventProperties: EventSurveyCompletedWithKit = {
      kit_id: kitId,
      form_id: typeformId,
      tinyaccount_id: tinyAccountId,
    };
    // Set flag immediately to prevent redirect during mutation
    setSurveyFinishedNow(true);
    try {
      // Wait for mutation to complete before navigating
      await submitSurveyMutation.mutateAsync(eventProperties);
      if (!kitId) {
        tinyAccountId
          ? navigate(`/`, { state: { tinyAccountId } })
          : navigate('/');
        return;
      }
      if (!isRedesignedActivationEnabled) {
        // Pre-ENG-4942 behavior: no auto-progression between bundles and no
        // completion screen — always drop back on the landing screen.
        navigate(`/information/${kitId}`);
        return;
      }
      if (!nextIncompleteSurvey) {
        // Every health-history bundle is done — show the activation
        // completion screen instead of returning to the forms landing screen.
        navigate(`/information/${kitId}/complete`);
        return;
      }
      // Auto-progress straight into the next bundle instead of dropping the
      // user back on the landing screen; show what's next for a beat first.
      setNextSurveyToShow(nextIncompleteSurvey);
      transitionTimeoutRef.current = setTimeout(() => {
        navigate(
          `/typeform/${tinyAccountId}/${kitId}/${nextIncompleteSurvey.typeform_survey_id}`,
          { replace: true },
        );
      }, NEXT_SURVEY_TRANSITION_DELAY_MS);
    } catch (error) {
      setSurveyFinishedNow(false);
    }
  };

  const titleHeader = !nextSurveyToShow && !!currentTypeformSurvey?.name && (
    <Header title={currentTypeformSurvey.name} />
  );

  onSubmitRef.current = onSubmitTypeform;

  const widget = (
    <div
      className={c(
        styles.widgetContainer,
        isRedesignedActivationEnabled && styles.widgetContainerFill,
      )}
    >
      {nextSurveyToShow ? (
        <HealthFormTransition
          nextLabel={nextSurveyToShow.name}
          nextTimeEstimate={nextSurveyToShow.time_estimate ?? undefined}
        />
      ) : surveyFinishedNow ? (
        <div className='flex items-center justify-center'>
          <Typography variant='body-s'>Processing survey...</Typography>
        </div>
      ) : (
        <Widget
          id={typeformId}
          style={{ width: '100%', height: '100%' }}
          hidden={formProperties}
          onSubmit={handleTypeformSubmit}
          inlineOnMobile
          hideHeaders
        />
      )}
    </div>
  );

  return (
    <>
      {isSurveyCompleted && !surveyFinishedNow && (
        <Navigate to={kitId ? `/information/${kitId}` : '/'} replace />
      )}
      {!nextSurveyToShow && (
        <ExitFormsWarningModal
          isOpen={isExitModalOpen}
          variant='survey'
          onCancel={() => setIsExitModalOpen(false)}
          onConfirm={() => navigate(kitId ? `/information/${kitId}` : '/')}
        />
      )}

      {isRedesignedActivationEnabled ? (
        <ActivationScreen
          stretchBody
          onBack={
            nextSurveyToShow ? undefined : () => setIsExitModalOpen(true)
          }
          header={
            <div className={styles.screenHeader}>
              {!nextSurveyToShow && !!currentTypeformSurvey?.name && (
                <Typography
                  variant='heading-m'
                  as='h2'
                  className={styles.formTitle}
                >
                  {currentTypeformSurvey.name}
                </Typography>
              )}
              <HealthFormsStepBar
                total={HEALTH_FORMS_TOTAL}
                currentIndex={HEALTH_HISTORY_INDEX}
                label='Health history'
                className={styles.stepBar}
              />
            </div>
          }
          footer={
            hasFinishLaterFooter && (
              <div className={styles.finishLaterRow}>
                <DesignButton
                  label='Finish later'
                  variant={ButtonVariants.TEXT}
                  color={ButtonColors.DARK}
                  size={ButtonSizes.M}
                  disabled={surveyFinishedNow}
                  onClick={() => setIsExitModalOpen(true)}
                />
              </div>
            )
          }
        >
          {widget}
        </ActivationScreen>
      ) : (
        <>
          {titleHeader}
          {widget}
        </>
      )}
    </>
  );
};

export default TypeformSurvey;
